/**
 * Utility to manage a queue of promises with concurrency control,
 * throttling, and automatic retry for 429 (Rate Limit) errors.
 */

class RequestQueue {
  constructor(concurrency = 5, delayBetween = 50) {
    this.concurrency = concurrency;
    this.delayBetween = delayBetween;
    this.queue = [];
    this.activeCount = 0;
  }

  /**
   * Check if the queue is currently idle (no active or pending requests).
   * @returns {boolean}
   */
  isIdle() {
    return this.activeCount === 0 && this.queue.length === 0;
  }

  /**
   * Clear all pending requests in the queue.
   */
  clear() {
    this.queue.forEach(item => item.reject(new Error("Queue cleared")));
    this.queue = [];
  }

  /**
   * Add a request to the queue.
   * @param {Function} requestFn - A function that returns a promise.
   * @param {Object} options - Retry options.
   * @returns {Promise}
   */
  add(requestFn, options = { maxRetries: 3, initialDelay: 1000 }) {
    return new Promise((resolve, reject) => {
      this.queue.push({
        requestFn,
        options,
        resolve,
        reject,
        retries: 0
      });
      this.process();
    });
  }

  async process() {
    if (this.activeCount >= this.concurrency || this.queue.length === 0) {
      return;
    }

    const item = this.queue.shift();
    this.activeCount++;

    // Small delay between starting requests to avoid bursting
    if (this.delayBetween > 0) {
      await new Promise(r => setTimeout(r, this.delayBetween));
    }

    this.execute(item);
    
    // Check if we can start another one immediately
    this.process();
  }

  async execute(item) {
    try {
      const result = await item.requestFn();
      item.resolve(result);
    } catch (error) {
      // Handle 429 Rate Limit error
      if (error.response && error.response.status === 429 && item.retries < item.options.maxRetries) {
        item.retries++;
        
        // Exponential backoff with jitter
        const delay = item.options.initialDelay * Math.pow(2, item.retries - 1) + Math.random() * 1000;
        console.warn(`[RequestQueue] 429 Rate Limit hit. Retrying in ${Math.round(delay)}ms... (Attempt ${item.retries}/${item.options.maxRetries})`);
        
        setTimeout(() => {
          this.queue.push(item);
          this.process();
        }, delay);
      } else {
        item.reject(error);
      }
    } finally {
      this.activeCount--;
      if (this.activeCount === 0 && this.queue.length === 0) {
        window.dispatchEvent(new CustomEvent("requestQueueIdle"));
      }
      this.process();
    }
  }
}

// Export a singleton instance for global use
const requestQueue = new RequestQueue(5, 50);
export default requestQueue;
